import { useState } from 'react';
import classNames from 'classnames';
import { StyledTooltip } from '../tooltip/Tooltip';
import { AiFillEyeInvisible, AiFillEye } from 'react-icons/ai';
import { FormField } from './FormField';
import type { FormFieldProps } from './FormField';
import type { ControllerFieldState } from 'react-hook-form';
import { Input, type InputProps } from 'antd';

export type TextFieldProps = Omit<FormFieldProps, 'prefix'> &
    InputProps & {
        showViewPasswordIcon?: boolean;
        fieldState?: ControllerFieldState;
    };

export const TextField = ({
    fieldState,
    type = 'text',
    label = '',
    disabled = false,
    showViewPasswordIcon = false,
    showValidationIcon,
    error,
    correct,
    prefix,
    ...props
}: TextFieldProps) => {
    const errorMessage = error || fieldState?.error?.message;
    const isValid = correct || (fieldState?.isDirty && !fieldState?.error?.message);
    const [showPassword, setShowPassword] = useState(false);

    const getInputClassName = () => {
        const inputClasses = classNames('input', {
            incorrect: errorMessage,
            correct: isValid,
        });
        return inputClasses;
    };

    const renderViewPasswordIcon = () => {
        if (showViewPasswordIcon) {
            return (
                <div className="input-icon">
                    <StyledTooltip title="Toggle Password">
                        <a
                            className="button view-password icon-only navy-medium"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowPassword(!showPassword);
                            }}>
                            {showPassword ? (
                                <AiFillEye
                                    className={`pw-visible ${type == 'password' ? 'active' : ''}`}
                                />
                            ) : (
                                <AiFillEyeInvisible
                                    className={`pw-hidden ${type == 'text' ? 'active' : ''}`}
                                />
                            )}
                            <span className="screen-reader-text">View Password</span>
                        </a>
                    </StyledTooltip>
                </div>
            );
        }

        return null;
    };

    return (
        <FormField
            {...props}
            fieldState={fieldState}
            label={label}
            labelFor={props?.id || props.name}
            error={error}
            correct={correct}
            showValidationIcon={showValidationIcon}>
            <Input
                {...props}
                id={props?.id || props.name}
                className={getInputClassName()}
                type={showPassword ? 'text' : type}
                aria-invalid={fieldState?.invalid ? 'true' : 'false'}
                disabled={disabled}
                prefix={prefix}
            />
            {renderViewPasswordIcon()}
        </FormField>
    );
};
